import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useAnchorToHashOnScroll, useHash } from 'src/hooks/hash';
import { useMediaQuerySSR } from 'src/hooks/useStateSSR';
import theme from 'src/utils/theme';
import { msgs } from './messages';
import * as S from './styles';

type AnchorLink = { id: string; text: string };

type TableOfContentsProps = {
  className?: string;
  orderedLinks: AnchorLink[];
  unorderedLinks: AnchorLink[];
};

export const TableOfContents = ({
  className,
  orderedLinks,
  unorderedLinks,
}: TableOfContentsProps) => {
  const hash = useHash();
  const hashChangesDisabled = !useMediaQuerySSR(false, {
    minWidth: theme.sz.xxl,
  });
  const intl = useIntl();

  const activeHash = useMemo(
    () => (hashChangesDisabled ? null : hash),
    [hash, hashChangesDisabled],
  );

  const anchors = useMemo(
    () => [...orderedLinks, ...unorderedLinks].map(({ id }) => id),
    [orderedLinks, unorderedLinks],
  );

  useAnchorToHashOnScroll(anchors, { disabled: hashChangesDisabled });

  return (
    <S.TableOfContents className={className} data-testid="tableOfContents">
      <S.Title>{intl.formatMessage(msgs.tableOfContents)}</S.Title>

      <S.Navigation>
        <S.OrderedList>
          {orderedLinks.map(({ id, text }) => (
            <S.ListItem key={id} $active={activeHash === id}>
              <S.Link href={`#${id}`}>{text}</S.Link>
            </S.ListItem>
          ))}
        </S.OrderedList>

        <S.UnorderedList>
          {unorderedLinks.map(({ id, text }) => (
            <S.ListItem key={id} $active={activeHash === id}>
              <S.Link href={`#${id}`}>{text}</S.Link>
            </S.ListItem>
          ))}
        </S.UnorderedList>
      </S.Navigation>
    </S.TableOfContents>
  );
};
